import { useState, useEffect } from "react";
import { Radio, Calendar, Play, X, ExternalLink, Video, Sparkles } from "lucide-react";

// Convierte una URL de YouTube / Vimeo a su URL embebible. Si no se reconoce, devuelve null.
const getEmbedUrl = (url) => {
  if (!url) return null;
  const u = url.trim();
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|live\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

// Miniatura real de YouTube (si aplica)
const getThumbnail = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|live\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`;
  return null;
};

const isNew = (createdAt) => {
  if (!createdAt) return false;
  try {
    const d = new Date(createdAt);
    return (Date.now() - d.getTime()) < 7 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

const formatDate = (d) => {
  if (!d) return "";
  try {
    const dt = new Date(d + "T00:00:00");
    return dt.toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return d;
  }
};

const RecordingCard = ({ rec, onPlay }) => {
  const embed = getEmbedUrl(rec.url);
  const thumb = rec.thumbnail || getThumbnail(rec.url);
  const nuevo = isNew(rec.created_at);
  return (
    <div
      data-testid={`recording-card-${rec.id}`}
      className="group relative rounded-2xl overflow-hidden bg-[#0f0f1e] border border-white/10 hover:border-[#C9A84C]/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-15px_rgba(201,168,76,0.3)] flex flex-col"
    >
      <button
        type="button"
        onClick={() => onPlay(rec)}
        data-testid={`recording-play-${rec.id}`}
        className="relative aspect-video w-full bg-gradient-to-br from-[#13132a] to-[#0a0a14] flex items-center justify-center overflow-hidden"
      >
        {thumb ? (
          <img
            src={thumb}
            alt={rec.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_30%,#C9A84C,transparent_60%)]" />
        )}
        <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
        <span className="relative z-10 flex items-center justify-center w-16 h-16 rounded-full bg-[#C9A84C] text-[#0a0a14] shadow-lg transition-transform duration-300 group-hover:scale-110">
          <Play className="h-7 w-7 ml-0.5" fill="currentColor" />
        </span>
        <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 bg-black/60 backdrop-blur text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full border border-white/10">
          <Radio className="h-3 w-3 text-[#C9A84C]" /> Grabación
        </span>
        {nuevo && (
          <span
            data-testid={`recording-new-${rec.id}`}
            className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 bg-[#80C753] text-[#0a0a14] text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full shadow-lg animate-pulse"
          >
            <Sparkles className="h-3 w-3" /> Nuevo
          </span>
        )}
      </button>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-1.5 text-[11px] text-[#C9A84C] font-mono uppercase tracking-wide mb-2">
          <Calendar className="h-3 w-3" />
          {formatDate(rec.recorded_date)}
        </div>
        <h3 className="text-base font-black text-white leading-snug mb-1.5">{rec.title}</h3>
        {rec.description && (
          <p className="text-xs text-slate-400 leading-relaxed mb-3 line-clamp-3">{rec.description}</p>
        )}
        <div className="mt-auto pt-2 flex items-center justify-between">
          {rec.presenter ? (
            <span className="text-[11px] text-slate-500">{rec.presenter}</span>
          ) : <span />}
          <button
            type="button"
            onClick={() => (embed ? onPlay(rec) : window.open(rec.url, "_blank"))}
            data-testid={`recording-watch-${rec.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#C9A84C] hover:text-white transition-colors"
          >
            {embed ? <><Play className="h-3.5 w-3.5" /> Ver grabación</> : <><ExternalLink className="h-3.5 w-3.5" /> Abrir</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ZoomRecordingsSection = ({ recordings = [], cursoMeta = {}, searching = false, query = "" }) => {
  const [active, setActive] = useState(null); // grabación en reproducción (modal)

  // Cerrar modal con ESC
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => { if (e.key === "Escape") setActive(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  if (!recordings || recordings.length === 0) return null;

  const activeEmbed = active ? getEmbedUrl(active.url) : null;

  // Agrupar por curso/secreto (solo cuando NO se está buscando)
  const groups = [];
  if (!searching) {
    const map = {};
    recordings.forEach((r) => {
      const key = r.secreto ? r.secreto : "__general";
      (map[key] = map[key] || []).push(r);
    });
    const known = Object.keys(cursoMeta).filter((k) => map[k]);
    const otros = Object.keys(map).filter((k) => k !== "__general" && !cursoMeta[k]);
    [...known, ...otros].forEach((k) => {
      const meta = cursoMeta[k];
      groups.push({ key: k, label: meta ? meta.title : k, subtitle: meta ? meta.subtitle : "", accent: meta ? meta.accent : "#C9A84C", items: map[k] });
    });
    if (map["__general"]) {
      groups.push({ key: "__general", label: "Grabaciones generales", subtitle: "", accent: "#C9A84C", items: map["__general"] });
    }
  }

  return (
    <section className="px-4 py-12 sm:py-16" data-testid="academia-recordings-section" id="grabaciones">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-2">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#C9A84C]/30 to-[#C9A84C]/30" />
          <Video className="h-5 w-5 text-[#C9A84C]" />
          <div className="h-px flex-1 bg-gradient-to-l from-transparent via-[#C9A84C]/30 to-[#C9A84C]/30" />
        </div>
        <div className="text-center mb-10">
          <p className="inline-flex items-center gap-2 text-[11px] text-[#C9A84C] font-mono tracking-[3px] uppercase mb-3">
            <Radio className="h-3.5 w-3.5" /> Biblioteca · En vivo
          </p>
          <h2 className="text-3xl sm:text-4xl font-black text-white">
            {searching ? `Grabaciones para "${query.trim()}"` : "Grabaciones en vivo / Zoom"}
          </h2>
          <p className="text-slate-400 text-sm mt-3 max-w-xl mx-auto">
            {searching
              ? `${recordings.length} ${recordings.length === 1 ? "grabación encontrada" : "grabaciones encontradas"}.`
              : "Revive cada sesión en vivo cuando quieras. Organizadas por curso y ordenadas por fecha."}
          </p>
        </div>

        {searching ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {recordings.map((rec) => (
              <RecordingCard key={rec.id} rec={rec} onPlay={setActive} />
            ))}
          </div>
        ) : (
          <div className="space-y-12">
            {groups.map((g) => (
              <div key={g.key} data-testid={`recording-group-${g.key}`}>
                <div className="flex items-center gap-3 mb-5">
                  <span className="inline-block w-1.5 h-6 rounded-full" style={{ backgroundColor: g.accent }} />
                  <div>
                    <h3 className="text-lg sm:text-xl font-black text-white leading-none">{g.label}</h3>
                    {g.subtitle && <p className="text-xs text-slate-500 mt-1">{g.subtitle}</p>}
                  </div>
                  <span className="ml-auto text-[11px] font-bold text-slate-500 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">
                    {g.items.length} {g.items.length === 1 ? "grabación" : "grabaciones"}
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {g.items.map((rec) => (
                    <RecordingCard key={rec.id} rec={rec} onPlay={setActive} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal reproductor */}
      {active && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setActive(null)}
          data-testid="recording-modal"
        >
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[11px] text-[#C9A84C] font-mono uppercase tracking-wide flex items-center gap-1.5">
                  <Calendar className="h-3 w-3" /> {formatDate(active.recorded_date)}
                </p>
                <h3 className="text-lg font-black text-white">{active.title}</h3>
              </div>
              <button
                type="button"
                onClick={() => setActive(null)}
                data-testid="recording-modal-close"
                aria-label="Cerrar"
                className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="aspect-video w-full rounded-2xl overflow-hidden bg-black border border-white/10">
              {activeEmbed ? (
                <iframe
                  src={`${activeEmbed}?autoplay=1`}
                  title={active.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="recording-iframe"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-center p-6">
                  <p className="text-slate-300 text-sm">Esta grabación se abre en una pestaña nueva.</p>
                  <a
                    href={active.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#C9A84C] text-[#0a0a14] font-bold px-5 py-2.5 rounded-xl hover:opacity-90 transition-opacity"
                  >
                    <ExternalLink className="h-4 w-4" /> Abrir grabación
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ZoomRecordingsSection;
